import api from '@/lib/api';
import type {
  Candidate,
  CandidateWithDetails,
  CreateCandidateDto,
  UpdateCandidateDto,
  CandidateFilters,
  PaginationParams,
  PaginatedResponse,
  SemanticSearchResponse,
  CandidateSkill,
  AddCandidateSkillDto,
  UpdateCandidateSkillDto,
  CandidateExperience,
  CreateExperienceDto,
  UpdateExperienceDto,
  CandidateEducation,
  CreateEducationDto,
  UpdateEducationDto,
} from '../types';

const BASE_URL = '/itc/candidates';

export interface CandidateStats {
  total: number;
  availableNow: number;
  addedThisMonth: number;
  withoutEmbedding: number;
}

/**
 * Candidates API service
 */
export const candidatesService = {
  /**
   * Get candidates statistics
   */
  async getStats(): Promise<CandidateStats> {
    const { data } = await api.get(`${BASE_URL}/stats`);
    return data;
  },

  /**
   * List candidates with pagination and filters
   */
  async list(
    filters: CandidateFilters = {},
    pagination: PaginationParams = {}
  ): Promise<PaginatedResponse<Candidate>> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.seniority) params.append('seniority', filters.seniority);
    if (filters.availability) params.append('availability', filters.availability);
    if (filters.locationCity) params.append('locationCity', filters.locationCity);
    if (filters.regionId) params.append('regionId', String(filters.regionId));
    if (filters.departementId) params.append('departementId', String(filters.departementId));
    if (filters.contractPreference) params.append('contractPreference', filters.contractPreference);
    if (filters.isFavorite !== undefined) params.append('isFavorite', String(filters.isFavorite));

    if (pagination.page) params.append('page', String(pagination.page));
    if (pagination.pageSize) params.append('pageSize', String(pagination.pageSize));
    if (pagination.sortBy) params.append('sortBy', pagination.sortBy);
    if (pagination.sortOrder) params.append('sortOrder', pagination.sortOrder);

    const { data } = await api.get(`${BASE_URL}?${params.toString()}`);
    // Transformer la réponse plate du backend en structure PaginatedResponse
    return {
      data: data.data,
      pagination: {
        page: data.page,
        pageSize: data.pageSize,
        total: data.total,
        totalPages: data.totalPages,
      },
    };
  },

  /**
   * Get candidate by ID
   */
  async getById(id: number): Promise<Candidate> {
    const { data } = await api.get(`${BASE_URL}/${id}`);
    return data;
  },

  /**
   * Get candidate with all details (skills, experiences, educations)
   */
  async getWithDetails(id: number): Promise<CandidateWithDetails> {
    const [candidate, skills, experiences, educations, documents] = await Promise.all([
      this.getById(id),
      this.getSkills(id),
      this.getExperiences(id),
      this.getEducations(id),
      this.getDocuments(id),
    ]);

    return {
      ...candidate,
      skills,
      experiences,
      educations,
      documents,
    };
  },

  /**
   * Create a new candidate
   */
  async create(dto: CreateCandidateDto): Promise<Candidate> {
    const { data } = await api.post(BASE_URL, dto);
    return data;
  },

  /**
   * Update a candidate
   */
  async update(id: number, dto: UpdateCandidateDto): Promise<Candidate> {
    const { data } = await api.patch(`${BASE_URL}/${id}`, dto);
    return data;
  },

  /**
   * Toggle favorite state (shared at company level)
   */
  async toggleFavorite(id: number, isFavorite: boolean): Promise<Candidate> {
    const { data } = await api.patch(`${BASE_URL}/${id}/favorite`, { isFavorite });
    return data;
  },

  /**
   * Delete a candidate (soft delete)
   */
  async delete(id: number): Promise<void> {
    await api.delete(`${BASE_URL}/${id}`);
  },

  // Skills
  async getSkills(candidateId: number): Promise<CandidateSkill[]> {
    const { data } = await api.get(`${BASE_URL}/${candidateId}/skills`);
    return data;
  },

  async addSkill(candidateId: number, dto: AddCandidateSkillDto): Promise<CandidateSkill> {
    const { data } = await api.post(`${BASE_URL}/${candidateId}/skills`, dto);
    return data;
  },

  async updateSkill(candidateId: number, skillId: number, dto: UpdateCandidateSkillDto): Promise<CandidateSkill> {
    const { data } = await api.patch(`${BASE_URL}/${candidateId}/skills/${skillId}`, dto);
    return data;
  },

  async removeSkill(candidateId: number, skillId: number): Promise<void> {
    await api.delete(`${BASE_URL}/${candidateId}/skills/${skillId}`);
  },

  // Experiences
  async getExperiences(candidateId: number): Promise<CandidateExperience[]> {
    const { data } = await api.get(`${BASE_URL}/${candidateId}/experiences`);
    return data;
  },

  async addExperience(candidateId: number, dto: CreateExperienceDto): Promise<CandidateExperience> {
    const { data } = await api.post(`${BASE_URL}/${candidateId}/experiences`, dto);
    return data;
  },

  async updateExperience(candidateId: number, experienceId: number, dto: UpdateExperienceDto): Promise<CandidateExperience> {
    const { data } = await api.patch(`${BASE_URL}/${candidateId}/experiences/${experienceId}`, dto);
    return data;
  },

  async removeExperience(candidateId: number, experienceId: number): Promise<void> {
    await api.delete(`${BASE_URL}/${candidateId}/experiences/${experienceId}`);
  },

  // Educations
  async getEducations(candidateId: number): Promise<CandidateEducation[]> {
    const { data } = await api.get(`${BASE_URL}/${candidateId}/educations`);
    return data;
  },

  async addEducation(candidateId: number, dto: CreateEducationDto): Promise<CandidateEducation> {
    const { data } = await api.post(`${BASE_URL}/${candidateId}/educations`, dto);
    return data;
  },

  async updateEducation(candidateId: number, educationId: number, dto: UpdateEducationDto): Promise<CandidateEducation> {
    const { data } = await api.patch(`${BASE_URL}/${candidateId}/educations/${educationId}`, dto);
    return data;
  },

  async removeEducation(candidateId: number, educationId: number): Promise<void> {
    await api.delete(`${BASE_URL}/${candidateId}/educations/${educationId}`);
  },

  // Documents
  async getDocuments(candidateId: number) {
    const { data } = await api.get(`${BASE_URL}/${candidateId}/documents`);
    return data;
  },

  /**
   * Upload CV document
   */
  async uploadCV(candidateId: number, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'cv');

    const { data } = await api.post(`${BASE_URL}/${candidateId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  /**
   * Extract and apply CV data
   */
  async extractCV(candidateId: number, documentId: number): Promise<any> {
    const { data } = await api.post(`${BASE_URL}/${candidateId}/documents/${documentId}/extract`);
    return data;
  },

  /**
   * Generate embedding for candidate
   */
  async generateEmbedding(candidateId: number): Promise<any> {
    const { data } = await api.post(`${BASE_URL}/${candidateId}/embedding`);
    return data;
  },

  /**
   * Semantic search
   */
  async semanticSearch(
    query: string,
    options: { limit?: number; minSimilarity?: number; status?: string; seniority?: string; locationCity?: string } = {}
  ): Promise<SemanticSearchResponse> {
    const params = new URLSearchParams();
    params.append('query', query);
    if (options.limit) params.append('limit', String(options.limit));
    if (options.minSimilarity) params.append('minSimilarity', String(options.minSimilarity));
    if (options.status) params.append('status', options.status);
    if (options.seniority) params.append('seniority', options.seniority);
    if (options.locationCity) params.append('locationCity', options.locationCity);

    const { data } = await api.get(`${BASE_URL}/search/semantic?${params.toString()}`);
    return data;
  },

  /**
   * Find similar candidates
   */
  async findSimilar(
    candidateId: number,
    options: { limit?: number; minSimilarity?: number } = {}
  ): Promise<SemanticSearchResponse['results']> {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', String(options.limit));
    if (options.minSimilarity) params.append('minSimilarity', String(options.minSimilarity));

    const { data } = await api.get(`${BASE_URL}/search/similar/${candidateId}?${params.toString()}`);
    return data;
  },
};
